import React, { useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from './context/auth/CognitoAuthContext';

// Layout components
import Layout from './components/layout/Layout';

// Pages
import LoginPage from './pages/LoginPage';
import InfluencerSettingsPage from './pages/InfluencerSettingsPage';
import RawDatasetPage from './pages/RawDatasetPage';
import TrainingSettingsPage from './pages/TrainingSettingsPage';
import NonMLFilteredPage from './pages/NonMLFilteredPage';
import CroppedPage from './pages/CroppedPage';
import CroppedCaptionedPage from './pages/CroppedCaptionedPage';
import AboutPage from './pages/AboutPage';
import InvitationPage from './pages/InvitationPage';

// Protected route component with navigation state tracking
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const navigationInProgressRef = useRef(false);
  
  // Show loading state while authentication is being checked
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column'
      }}>
        <div style={{ marginBottom: '16px' }}>Loading...</div>
      </div>
    );
  }
  
  // Prevent multiple simultaneous navigation attempts
  if (!isAuthenticated && !navigationInProgressRef.current) {
    navigationInProgressRef.current = true;
    // Save the current location they were trying to access for post-login redirect
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // Reset navigation flag when authenticated
  if (isAuthenticated) {
    navigationInProgressRef.current = false;
  }
  
  return children;
};

// Theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#f50057',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 500,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastContainer position="top-right" autoClose={5000} />
      <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            {/* Main route is influencer list */}
            <Route index element={<InfluencerSettingsPage />} />

            {/* Tabs shown when influencer is selected */}
            <Route path="influencer/:id" element={<InfluencerSettingsPage />}>
              <Route index element={<AboutPage />} />
              <Route path="about" element={<AboutPage />} />
              <Route path="raw-dataset" element={<RawDatasetPage />} />
              <Route path="cropped" element={<CroppedPage />} />
              <Route path="cropped-captioned" element={<CroppedCaptionedPage />} />
              <Route path="training-settings" element={<TrainingSettingsPage />} />
            </Route>

            {/* Standalone routes */}
            <Route path="cropped-captioned" element={<CroppedCaptionedPage />} />
            <Route path="invitation" element={<InvitationPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    </ThemeProvider>
  );
}

export default App;
